import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { BookOpen, Heart, Users, Mic, GraduationCap, Cross, Shield, Globe, Flame } from "lucide-react";

const WORSHIP_IMG = "https://customer-assets.emergentagent.com/job_033adeb0-4821-466d-a137-c74b40513737/artifacts/595q5euy_Shevat2026-2601187465.jpg";

const FIVEFOLD = [
  { icon: Shield, title: "Apostle", desc: "Building the foundation and governing structure of the ministry" },
  { icon: Mic, title: "Prophet", desc: "Speaking God's truth and direction for the body of Christ" },
  { icon: Globe, title: "Evangelist", desc: "Reaching the lost and sharing the good news" },
  { icon: Heart, title: "Pastor", desc: "Shepherding and caring for God's people" },
  { icon: GraduationCap, title: "Teacher", desc: "Equipping the saints with knowledge and understanding" },
];

const FAITH_STATEMENTS = [
  "We believe the Bible is the inspired, infallible Word of God.",
  "We believe in one God, eternally existent in three persons: Father, Son, and Holy Spirit.",
  "We believe in the deity of our Lord Jesus Christ, His virgin birth, His sinless life, His miracles, His atoning death, His bodily resurrection, and His ascension to the right hand of the Father.",
  "We believe in the present ministry of the Holy Spirit, by whose indwelling the Christian is enabled to live a godly life.",
  "We believe in the spiritual unity of believers in our Lord Jesus Christ.",
  "We believe in the resurrection of both the saved and the lost; they that are saved unto the resurrection of life, and they that are lost unto the resurrection of damnation.",
  "We believe in the baptism of the Holy Spirit with the evidence of speaking in tongues as the Spirit gives utterance.",
  "We believe that God has set in the church the five-fold ministry gifts of apostle, prophet, evangelist, pastor, and teacher for the equipping of the saints.",
];

export default function AboutPage() {
  return (
    <Layout>
      {/* Hero */}
      <section className="relative bg-navy py-20 text-white">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${WORSHIP_IMG})` }} />
        <div className="relative container-page text-center">
          <div className="gold-line-center mb-4" />
          <h1 className="font-heading text-4xl sm:text-5xl mb-4">About Us</h1>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Led by Apostle Dayna Milam, engaging the region for advancement through the five-fold ministry
          </p>
        </div>
      </section>

      {/* Mission */}
      <section data-testid="mission-section" className="py-20 bg-bone">
        <div className="container-narrow text-center">
          <div className="gold-line-center mb-4" />
          <h2 className="font-heading text-3xl md:text-4xl text-navy mb-6">Our Mission</h2>
          <p className="text-gray-600 text-lg leading-relaxed mb-6">
            Word Alive Global Center is a 501(c)(3) 5-fold Christian ministry based in Carthage, Texas. Our mission is to engage the region for advancement by equipping believers, reaching the lost, and transforming communities through the power of God's Word.
          </p>
          <p className="text-gray-600 text-lg leading-relaxed mb-6">
            Through worship, teaching, outreach, and service, we seek to build the Kingdom of God and raise up a generation that walks in the fullness of their calling.
          </p>
          <Separator className="my-8 max-w-xs mx-auto" />
          <h3 className="font-heading text-xl text-navy mb-3">Our Vision</h3>
          <p className="text-gray-600 leading-relaxed">
            One Unified Body under apostolic headship — walking in Daily Discipleship, Financial Sovereignty, and zero dependence on Big-Tech platforms. Every Collective is an extension of the Center, not a separate entity. We are building a Sovereign Digital Fortress where the Body of Christ can worship, connect, give, and grow without fragmentation or compromise.
          </p>
        </div>
      </section>

      {/* Leadership */}
      <section data-testid="leadership-section" className="py-20 bg-white">
        <div className="container-page">
          <div className="text-center mb-12">
            <div className="gold-line-center mb-4" />
            <h2 className="font-heading text-3xl md:text-4xl text-navy">Our Leadership</h2>
          </div>

          {/* Dayna - Primary Leader */}
          <div className="max-w-4xl mx-auto mb-12">
            <Card className="bg-bone border-none shadow-sm overflow-hidden">
              <CardContent className="p-10">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="flex-shrink-0">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_bible-ministry/artifacts/10xz33wa_dayna%20coral1.jpg"
                      alt="Apostle Dayna Milam"
                      className="w-64 h-80 object-cover object-top rounded-sm shadow-lg border-2 border-gold/40"
                      style={{ imageRendering: 'auto', WebkitBackfaceVisibility: 'hidden' }}
                      loading="eager"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=500&fit=crop";
                      }}
                    />
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="font-heading text-3xl text-navy mb-1">Apostle Dayna Milam</h3>
                    <p className="text-gold font-medium text-lg mb-5">Apostle / Senior Leader</p>
                    <div className="w-12 h-0.5 bg-gold mx-auto md:mx-0 mb-5" />
                    <p className="text-gray-600 leading-relaxed mb-3">
                      Apostle Dayna Milam is the visionary leader of Word Alive Global Center, carrying a mandate to engage the region for advancement through apostolic authority and the power of God's Word. She is a gifted teacher, author, and prophetic voice whose ministry encourages and anchors believers in hope.
                    </p>
                    <p className="text-gray-600 leading-relaxed">
                      Through Dayna Milam Ministries, she reaches women, families, and communities with the life-changing message of the Gospel. Her heart burns to see the body of Christ walk in the fullness of their God-given purpose and calling.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Craig - Godly Covering */}
          <div className="max-w-3xl mx-auto mb-12">
            <Card className="bg-bone border-none shadow-sm">
              <CardContent className="p-8 text-center">
                <div className="w-28 h-28 bg-navy rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-gold font-heading text-3xl">CM</span>
                </div>
                <h3 className="font-heading text-2xl text-navy mb-1">Craig Milam</h3>
                <p className="text-gold font-medium mb-4">Godly Covering & Husband</p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Craig Milam serves as the steadfast foundation behind the ministry. As Apostle Dayna's husband and Godly covering, Craig provides the support, wisdom, and structure that enables Word Alive Global Center to flourish. His quiet strength and faithfulness behind the scenes are the bedrock upon which this ministry stands.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Future Leaders Placeholder */}
          <div className="text-center">
            <p className="text-gray-400 text-sm italic">Additional ministry leaders coming soon</p>
          </div>
        </div>
      </section>

      {/* 5-Fold Ministry */}
      <section data-testid="fivefold-section" className="py-20 bg-bone">
        <div className="container-page">
          <div className="text-center mb-12">
            <div className="gold-line-center mb-4" />
            <h2 className="font-heading text-3xl md:text-4xl text-navy mb-4">The Five-Fold Ministry</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              "And He gave some as apostles, and some as prophets, and some as evangelists, and some as pastors and teachers, for the equipping of the saints." &mdash; Ephesians 4:11-12
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {FIVEFOLD.map((item) => (
              <Card key={item.title} className="bg-white border border-bone-dark hover:border-gold/50 transition-colors">
                <CardContent className="p-6 text-center">
                  <item.icon className="h-8 w-8 text-gold mx-auto mb-3" />
                  <h3 className="font-heading text-lg text-navy mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Statement of Faith */}
      <section data-testid="faith-section" className="py-20 bg-navy text-white">
        <div className="container-narrow">
          <div className="text-center mb-12">
            <Flame className="h-8 w-8 text-gold mx-auto mb-3" />
            <div className="gold-line-center mb-4" />
            <h2 className="font-heading text-3xl md:text-4xl">Statement of Faith</h2>
          </div>
          <div className="space-y-4">
            {FAITH_STATEMENTS.map((statement, i) => (
              <div key={i} className="flex gap-4 items-start">
                <span className="text-gold font-heading text-lg shrink-0 w-8">{i + 1}.</span>
                <p className="text-white/75 leading-relaxed">{statement}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
